import React from 'react';
import {
    Drawer, Box, Typography, IconButton, Divider, Stack, Avatar, Chip, Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CodeIcon from '@mui/icons-material/Code';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LabelIcon from '@mui/icons-material/Label';
import { format, isToday, isYesterday } from 'date-fns';
import { sk } from 'date-fns/locale';
import { usePreferences } from '../contexts/PreferencesContext';
import { getThemeColors } from '../config/theme';

const GitCommitDetailDrawer = ({ commit, open, onClose }) => {
    const { isDarkMode } = usePreferences();
    const colors = getThemeColors(isDarkMode);

    if (!commit) {
        return null;
    }

    const commitDate = new Date(commit.created_at);
    const formatDate = () => {
        if (isToday(commitDate)) {
            return `Dnes ${format(commitDate, 'HH:mm')}`;
        }
        if (isYesterday(commitDate)) {
            return `Včera ${format(commitDate, 'HH:mm')}`;
        }
        return format(commitDate, 'd.M.yyyy HH:mm', { locale: sk });
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const nameParts = name.trim().split(' ');
        if (nameParts.length > 1) {
            return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
        } else if (nameParts[0] && nameParts[0].length > 1) {
            return nameParts[0].substring(0, 2).toUpperCase();
        }
        return (name[0] || '').toUpperCase();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            keepMounted={false}
            disableRestoreFocus={true}
        >
            <Box sx={{ width: { xs: '100vw', sm: 400 }, p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                        <IconButton onClick={onClose}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Avatar sx={{ bgcolor: colors.primary, color: '#ffffff', width: 50, height: 50, fontSize: '1.2rem' }}>
                            <CodeIcon />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: colors.textPrimary }}>
                                Git Commit
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>

                <Divider sx={{ my: 2, borderColor: colors.border }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Commit Message */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600 }}>
                            Commit Message
                        </Typography>
                        <Typography variant="body1" sx={{ color: colors.textPrimary, lineHeight: 1.6 }}>
                            {commit.message || 'No message'}
                        </Typography>
                    </Box>

                    {/* Author */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: '1rem' }} />
                            Author
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: colors.info, color: '#ffffff', width: 32, height: 32, fontSize: '0.875rem' }}>
                                {getInitials(commit.author)}
                            </Avatar>
                            <Typography variant="body1" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                                {commit.author || 'Unknown'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Date */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ScheduleIcon sx={{ fontSize: '1rem' }} />
                            Date
                        </Typography>
                        <Typography variant="body1" sx={{ color: colors.textPrimary }}>
                            {formatDate()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                            {format(commitDate, 'EEEE, d. MMMM yyyy, HH:mm', { locale: sk })}
                        </Typography>
                    </Box>

                    {/* Hash */}
                    {commit.hash && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CodeIcon sx={{ fontSize: '1rem' }} />
                                Commit Hash
                            </Typography>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : colors.backgroundTertiary,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                    {commit.hash}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Tag */}
                    {commit.tag && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LabelIcon sx={{ fontSize: '1rem' }} />
                                Tag
                            </Typography>
                            <Chip
                                label={commit.tag}
                                size="small"
                                sx={{
                                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : colors.backgroundTertiary,
                                    color: colors.textTertiary,
                                    border: `1px solid ${colors.border}`,
                                }}
                            />
                        </Box>
                    )}

                    {/* URL Link */}
                    {commit.url && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                                View on Git
                            </Typography>
                            <Link
                                href={commit.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: colors.info,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                Open in new tab
                                <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                            </Link>
                        </Box>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default GitCommitDetailDrawer;
















